import { useEffect, useMemo, useState } from 'react';
import { Cpu, Layers } from 'lucide-react';

import { Spinner } from '../../components/common/index.jsx';
import { admin, work } from '../../lib/api.js';
import { markFinalCaption, markOriginalCaption } from '../../lib/agentCaption.js';

/**
 * Agent Outputs - the saved intermediate steps of the orchestration pipeline.
 *
 * A faithful port of the old dashboard's tab of the same name: the same five numbered
 * sections in the same order, the same cards and pills, the same three-way caption
 * comparison. Admin only; annotators must never see the agents' reasoning.
 *
 * The one structural change is data loading. The old version had the entire 22 MB
 * dashboard.json in memory, so it could pick any run instantly. Here the index is
 * fetched once and each run's payload (~70 KB) is fetched on selection.
 */

//: Dataset names come from the registry, never from a map baked in here - a hardcoded
//: key silently empties the page the moment a dataset is renamed or removed.
const ALL = '';

/* --- small presentational pieces, matching the originals ------------------------ */

const Pill = ({ value }) =>
  value == null || value === '' ? null : (
    <span className={`final-pill ${String(value).toLowerCase()}`}>
      {String(value).replaceAll('_', ' ')}
    </span>
  );

const Metric = ({ label, value }) => (
  <div className="final-metric">
    <span>{label}</span>
    <strong>{value || '-'}</strong>
  </div>
);

const TextBox = ({ children, html }) =>
  html !== undefined ? (
    // Built by lib/agentCaption.js, which escapes every value before wrapping it.
    <div className="final-text-box" dangerouslySetInnerHTML={{ __html: html }} />
  ) : (
    <div className="final-text-box">{children}</div>
  );

const Json = ({ value }) => (
  <pre className="final-json">{JSON.stringify(value ?? {}, null, 2)}</pre>
);

function SpanList({ spans = [] }) {
  if (!spans.length) return <div className="empty-state">No problematic spans listed.</div>;
  return spans.map((span, i) => (
    <div className="final-claim-card" key={`${span.span_text || span.claim_or_span}-${i}`}>
      <div className="final-claim-top">
        <span>{span.span_text || span.claim_or_span}</span>
        <Pill value={span.label || span.final_label} />
      </div>
      <div className="final-pill-row">
        <Pill value={span.action || span.final_action} />
      </div>
      {span.replacement && (
        <p><strong>Replacement:</strong> {span.replacement}</p>
      )}
      {span.uncertainty_note && (
        <p><strong>Uncertainty:</strong> {span.uncertainty_note}</p>
      )}
      {span.reason && <p className="final-reason">{span.reason}</p>}
    </div>
  ));
}

function DetailList({ items = [] }) {
  if (!items.length) return <div className="empty-state">No missing visible details listed.</div>;
  return items.map((item, i) => (
    <div className="final-claim-card" key={`${item.detail || item}-${i}`}>
      <div className="final-claim-top">
        <span>{item.detail || String(item)}</span>
      </div>
      {(item.evidence || item.reason) && (
        <p className="final-reason">{item.evidence || item.reason}</p>
      )}
    </div>
  ));
}

const JudgmentCard = ({ judgment }) => (
  <div className="final-claim-card">
    <div className="final-claim-top">
      <span>{judgment.agent_id || 'agent'}</span>
      <Pill value={judgment.label} />
    </div>
    <div className="final-pill-row">
      <Pill value={judgment.action} />
    </div>
    {judgment.replacement && <p><strong>Replacement:</strong> {judgment.replacement}</p>}
    {judgment.uncertainty_note && <p><strong>Uncertainty:</strong> {judgment.uncertainty_note}</p>}
    {judgment.reason && <p className="final-reason">{judgment.reason}</p>}
  </div>
);

const CONSENSUS_ROWS = [
  ['agreed_hallucinations', 'Agreed hallucinations'],
  ['disputed_hallucinations', 'Disputed hallucinations'],
  ['wrong_uncertainty_claims', 'Wrong uncertainty in caption'],
  ['rejected_agent_uncertainties', 'Rejected agent uncertainties'],
  ['uncertain_claims', 'Final uncertain claims'],
  ['unverifiable_claims', 'Unverifiable claims'],
  ['verified_missing_details', 'Verified missing details'],
  ['remaining_disagreements', 'Remaining disagreements'],
];

const ConsensusTable = ({ summary = {} }) => (
  <div className="table-scroll">
    <table className="data-table">
      <thead>
        <tr><th>Category</th><th>Items</th></tr>
      </thead>
      <tbody>
        {CONSENSUS_ROWS.map(([key, label]) => (
          <tr key={key}>
            <td>{label}</td>
            <td className="preline">{(summary[key] || []).join('\n') || '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CAPTION_LEGEND = [
  ['final-caption-removed', 'Hallucination/remove'],
  ['final-caption-unverifiable', 'Unverifiable'],
  ['final-caption-wrong', 'Wrong uncertainty'],
  ['final-caption-uncertain', 'Uncertainty note'],
  ['final-caption-replace', 'Original span selected for replacement'],
  ['final-caption-inserted', 'Replacement text'],
  ['final-caption-added', 'Added missing detail'],
  ['final-caption-supported', 'Kept/supported span'],
];

const CaptionLegend = () => (
  <div className="final-legend">
    {CAPTION_LEGEND.map(([cls, label]) => (
      <span className="final-legend-item" key={cls}>
        <span className={`final-legend-swatch ${cls}`} />
        {label}
      </span>
    ))}
  </div>
);

/* --- the page -------------------------------------------------------------------- */

export function AgentOutputsPage() {
  const [index, setIndex] = useState(null);
  const [dataset, setDataset] = useState(ALL);
  const [reviewFlag, setReviewFlag] = useState('all');
  const [taskId, setTaskId] = useState(null);
  const [sampleId, setSampleId] = useState(null);
  const [record, setRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    admin
      .pipelineIndex({ dataset: dataset || undefined, review_flag: reviewFlag })
      .then((data) => {
        setIndex(data);
        const first = data.images[0];
        setTaskId(first?.task_id ?? null);
        setSampleId(first?.captions?.[0]?.sample_id ?? null);
      })
      .catch(() => setIndex({ datasets: [], images: [], total_captions: 0, flagged_captions: 0 }));
  }, [dataset, reviewFlag]);

  const image = useMemo(
    () => index?.images.find((i) => i.task_id === taskId) || index?.images[0] || null,
    [index, taskId],
  );

  // Keep the caption selection valid when the image changes.
  useEffect(() => {
    if (!image) return;
    if (!image.captions.some((c) => c.sample_id === sampleId)) {
      setSampleId(image.captions[0]?.sample_id ?? null);
    }
  }, [image, sampleId]);

  useEffect(() => {
    if (!sampleId) {
      setRecord(null);
      return;
    }
    setLoadingRecord(true);
    work
      .pipeline(sampleId)
      .then(setRecord)
      .catch(() => setRecord(null))
      .finally(() => setLoadingRecord(false));
  }, [sampleId]);

  if (!index) return <Spinner label="Loading pipeline runs…" />;

  const datasets = index.datasets;
  const titleFor = (key) => datasets.find((d) => d.key === key)?.title || key;

  if (!image) {
    // Say which filter emptied the page and offer the way back. Without this the empty
    // state is a dead end: it carries no controls, so a filter that matches nothing
    // cannot be undone from here.
    const narrowed = dataset !== ALL || reviewFlag !== 'all';
    return (
      <div className="page wide">
        <div className="page-head">
          <h1>Agent Outputs</h1>
          <p>Saved intermediate steps of the orchestration pipeline.</p>
        </div>
        <section className="panel">
          <div className="empty-state">
            {narrowed ? (
              <>
                <p>
                  No orchestration rows for{' '}
                  <strong>{dataset === ALL ? 'any dataset' : titleFor(dataset)}</strong>
                  {reviewFlag === 'needed' && ' that the agent flagged for human review'}
                  {reviewFlag === 'not_needed' && ' that the agent did not flag'}.
                </p>
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => {
                    setDataset(ALL);
                    setReviewFlag('all');
                    setTaskId(null);
                    setSampleId(null);
                  }}
                >
                  Show all datasets
                </button>
              </>
            ) : (
              <p>
                No orchestration rows are loaded. Import a dataset with{' '}
                <code>scripts/import_datasets.py</code> to populate this view.
              </p>
            )}
          </div>
        </section>
      </div>
    );
  }

  const adjudication = record?.final_adjudication || {};
  const reviews = record?.independent_reviews || {};
  const claims = record?.private_claims_table || {};
  const finalSpans = adjudication.final_spans || [];
  const caption = image.captions.find((c) => c.sample_id === sampleId) || image.captions[0];

  return (
    <div className="page wide">
      <div className="page-head">
        <h1>Agent Outputs</h1>
        <p>
          Every saved step of the three-reviewer orchestration run, exactly as the pipeline
          recorded it. Admin only.
        </p>
      </div>

      <div className="final-orchestration-page">
        {/* ---- left: run picker ---- */}
        <section className="final-left panel">
          <div className="panel-title">
            <Layers size={18} />
            <h2>Runs</h2>
            <span>{index.total_captions} rows</span>
          </div>

          <div className="final-left-body">
            <div className="final-summary-grid">
              <Metric label="Rows" value={index.total_captions} />
              <Metric label="Images" value={index.images.length} />
              <Metric label="Human review" value={index.flagged_captions} />
              <Metric label="Output" value={record ? 'found' : '-'} />
            </div>

            <div className="final-controls">
              <div className="segmented">
                <button
                  type="button"
                  aria-pressed={dataset === ALL}
                  className={dataset === ALL ? 'active' : ''}
                  onClick={() => {
                    setDataset(ALL);
                    setTaskId(null);
                    setSampleId(null);
                  }}
                >
                  All
                </button>
                {datasets.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    aria-pressed={dataset === d.key}
                    className={dataset === d.key ? 'active' : ''}
                    onClick={() => {
                      setDataset(d.key);
                      setTaskId(null);
                      setSampleId(null);
                    }}
                  >
                    {d.title}
                  </button>
                ))}
              </div>

              <select
                value={image.task_id}
                onChange={(e) => setTaskId(Number(e.target.value))}
                aria-label="Image"
              >
                {index.images.map((img) => (
                  <option key={img.task_id} value={img.task_id}>
                    {titleFor(img.dataset_key)} · {img.image_id} · {img.captions.length} captions
                  </option>
                ))}
              </select>

              <select
                value={sampleId ?? ''}
                onChange={(e) => setSampleId(Number(e.target.value))}
                aria-label="Caption"
              >
                {image.captions.map((c) => (
                  <option key={c.sample_id} value={c.sample_id}>
                    {c.caption_source}
                    {c.human_review_flag ? ' · needs review' : ''}
                  </option>
                ))}
              </select>

              <select
                value={reviewFlag}
                onChange={(e) => {
                  setReviewFlag(e.target.value);
                  setTaskId(null);
                  setSampleId(null);
                }}
                aria-label="Review flag filter"
              >
                <option value="all">All samples</option>
                <option value="needed">Human review needed</option>
                <option value="not_needed">No human review needed</option>
              </select>
            </div>

            <div className="final-image-box">
              {image.image_missing ? (
                <div className="image-placeholder">No image</div>
              ) : (
                <img src={image.image_url} alt="" style={{ width: `${zoom * 100}%` }} />
              )}
            </div>

            <div className="final-zoom-row">
              <button type="button" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}>-</button>
              <button type="button" onClick={() => setZoom(1)}>Reset</button>
              <button type="button" onClick={() => setZoom((z) => Math.min(4, z + 0.25))}>+</button>
              <strong>{Math.round(zoom * 100)}%</strong>
            </div>

            <div className="annotation-meta">
              <p><strong>Dataset:</strong> {titleFor(image.dataset_key)}</p>
              <p><strong>Image ID:</strong> {image.image_id}</p>
              <p><strong>Image path:</strong> {image.image_path || '-'}</p>
            </div>
          </div>
        </section>

        {/* ---- right: the saved run ---- */}
        <section className="final-right panel">
          <div className="panel-title">
            <Cpu size={18} />
            <h2>Saved Intermediate Steps</h2>
            <span>{caption?.caption_source}</span>
          </div>

          {loadingRecord && <Spinner label="Loading run…" />}

          {!loadingRecord && !record && (
            <div className="empty-state">No saved orchestration output for this caption.</div>
          )}

          {!loadingRecord && record && (
            <div className="final-content">
              <section className="final-section">
                <div className="final-section-title">Run Overview</div>
                <div className="final-summary-grid">
                  <Metric label="Dataset" value={titleFor(record.dataset)} />
                  <Metric label="Image" value={record.image_id} />
                  <Metric label="Caption source" value={record.caption_source} />
                  <Metric label="Created" value={record.created_at} />
                </div>
                <p><strong>Caption key:</strong> {record.caption_key || '-'}</p>
                <p><strong>Reviewer agents:</strong> {JSON.stringify(record.agents?.reviewers || {})}</p>
                <p><strong>Claim table agent:</strong> {JSON.stringify(record.agents?.claim_table_agent || {})}</p>
                <p><strong>Adjudicator:</strong> {JSON.stringify(record.agents?.adjudicator || {})}</p>
              </section>

              <section className="final-section">
                <div className="final-section-title">Original Caption</div>
                <TextBox>{record.original_caption}</TextBox>
              </section>

              <section className="final-section">
                <div className="final-section-title">1. Independent Reviews</div>
                <div className="final-agent-grid">
                  {Object.entries(reviews).map(([agent, review]) => (
                    <article className="final-agent-card" key={agent}>
                      <div className="final-agent-head">
                        <strong>{agent}</strong>
                        <Pill value={review.needs_human_review ? 'needs human' : 'auto'} />
                      </div>
                      {review.review_notes && <p className="final-reason">{review.review_notes}</p>}
                      <details open>
                        <summary>Problematic spans</summary>
                        <SpanList spans={review.problematic_spans || []} />
                      </details>
                      <details>
                        <summary>Missing visible details</summary>
                        <DetailList items={review.missing_visible_details || []} />
                      </details>
                      <details>
                        <summary>Raw JSON</summary>
                        <Json value={review} />
                      </details>
                    </article>
                  ))}
                </div>
              </section>

              <section className="final-section">
                <div className="final-section-title">
                  2. Private Claims Table <span>{(claims.claim_table || []).length} rows</span>
                </div>
                {(claims.claim_table || []).map((claim, i) => (
                  <article className="final-claim-card" key={`${claim.claim_or_span}-${i}`}>
                    <div className="final-claim-top">
                      <span>{claim.claim_or_span}</span>
                      <Pill value={claim.agreement_status} />
                    </div>
                    <p><strong>Discussion needed:</strong> {String(claim.discussion_needed)}</p>
                    <div className="final-agent-grid">
                      {(claim.agent_judgments || []).map((judgment, j) => (
                        <JudgmentCard judgment={judgment} key={`${judgment.agent_id}-${j}`} />
                      ))}
                    </div>
                  </article>
                ))}
                <details>
                  <summary>
                    Missing details table ({(claims.missing_visible_details || []).length})
                  </summary>
                  <DetailList items={claims.missing_visible_details || []} />
                </details>
                <details>
                  <summary>Raw claims table JSON</summary>
                  <Json value={claims} />
                </details>
              </section>

              <section className="final-section">
                <div className="final-section-title">
                  3. Discussion Rounds <span>{(record.discussion_rounds || []).length} rounds</span>
                </div>
                {(record.discussion_rounds || []).map((round) => (
                  <article className="final-round-card" key={round.round_number}>
                    <div className="final-section-title">Round {round.round_number}</div>
                    <div className="final-agent-grid">
                      {Object.entries(round.agent_outputs || {}).map(([agent, output]) => (
                        <article className="final-agent-card" key={agent}>
                          <div className="final-agent-head">
                            <strong>{agent}</strong>
                            <Pill value={output.needs_extra_round ? 'asks extra round' : 'done'} />
                          </div>
                          {output.extra_round_reason && (
                            <p className="final-reason">{output.extra_round_reason}</p>
                          )}
                          <details open>
                            <summary>Discussion responses</summary>
                            {(output.discussion_responses || []).map((response, i) => (
                              <div className="final-claim-card" key={`${response.claim_or_span}-${i}`}>
                                <div className="final-claim-top">
                                  <span>{response.claim_or_span}</span>
                                  <Pill value={response.opinion_change || response.position} />
                                </div>
                                <div className="final-pill-row">
                                  <Pill value={response.stance_toward_others} />
                                  <Pill value={response.updated_label || response.revised_label} />
                                  <Pill value={response.updated_action || response.revised_action} />
                                </div>
                                {response.replacement && (
                                  <p><strong>Replacement:</strong> {response.replacement}</p>
                                )}
                                {response.uncertainty_note && (
                                  <p><strong>Uncertainty:</strong> {response.uncertainty_note}</p>
                                )}
                                {response.what_changed && (
                                  <p><strong>What changed:</strong> {response.what_changed}</p>
                                )}
                                {response.reason && <p className="final-reason">{response.reason}</p>}
                              </div>
                            ))}
                          </details>
                          <details>
                            <summary>Updated opinion table</summary>
                            {(output.updated_opinion_table || []).map((row, i) => (
                              <div className="final-claim-card" key={`${row.claim_or_span}-${i}`}>
                                <div className="final-claim-top">
                                  <span>{row.claim_or_span}</span>
                                  <Pill value={row.updated_label} />
                                </div>
                                <Pill value={row.updated_action} />
                                {row.replacement && <p><strong>Replacement:</strong> {row.replacement}</p>}
                                {row.uncertainty_note && (
                                  <p><strong>Uncertainty:</strong> {row.uncertainty_note}</p>
                                )}
                                {row.reason && <p className="final-reason">{row.reason}</p>}
                              </div>
                            ))}
                          </details>
                          <details>
                            <summary>Verified missing details</summary>
                            <DetailList items={output.verified_missing_details || []} />
                          </details>
                          <details>
                            <summary>Raw JSON</summary>
                            <Json value={output} />
                          </details>
                        </article>
                      ))}
                    </div>
                  </article>
                ))}
              </section>

              <section className="final-section">
                <div className="final-section-title">
                  4. Final Adjudication
                  <Pill value={adjudication.human_review_flag ? 'human review' : 'finalized'} />
                </div>
                <TextBox>{adjudication.final_edited_caption || ''}</TextBox>
                {adjudication.human_review_reason && (
                  <p className="final-reason">
                    <strong>Human review reason:</strong> {adjudication.human_review_reason}
                  </p>
                )}
                <details open>
                  <summary>Final spans ({finalSpans.length})</summary>
                  <SpanList spans={finalSpans} />
                </details>
                <details open>
                  <summary>Consensus summary</summary>
                  <ConsensusTable summary={adjudication.consensus_summary || {}} />
                </details>
                <details>
                  <summary>Raw final JSON</summary>
                  <Json value={adjudication} />
                </details>
              </section>

              <section className="final-section">
                <div className="final-section-title">5. Final Edited Caption</div>
                <CaptionLegend />
                <div className="final-caption-compare">
                  <div>
                    <h3>Original caption</h3>
                    <TextBox>{record.original_caption}</TextBox>
                  </div>
                  <div>
                    <h3>Model edits on original caption</h3>
                    <TextBox html={markOriginalCaption(record)} />
                  </div>
                  <div>
                    <h3>Final caption with added details</h3>
                    <TextBox
                      html={
                        adjudication.final_edited_caption
                          ? markFinalCaption(record)
                          : 'No model final caption saved for this row.'
                      }
                    />
                  </div>
                </div>
                <p className="muted">
                  The edit view marks the original spans selected by the final judge. The final
                  caption marks verified missing details when they can be matched in the edited
                  text.
                </p>
              </section>

              <section className="final-section">
                <div className="final-section-title">Raw Saved JSON</div>
                <details>
                  <summary>Show raw saved JSON</summary>
                  <Json value={record} />
                </details>
              </section>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
